import { createContext, useContext, useMemo } from 'react';
import type { RentalApplication, TermSetStatus, UserStatus } from '../graphql/queries';

// Context for when viewing a single application
export const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

export type FeatureFlags = Record<string, never>;

export interface ApplicationContextValue {
  application: RentalApplication;
  userStatuses: Array<UserStatus>;
  termSetStatus: TermSetStatus;
  featureFlags: FeatureFlags;
  isFirstTimeUser?: boolean;
}

export function ApplicationContextProvider({
  application,
  userStatuses,
  termSetStatus,
  featureFlags,
  isFirstTimeUser,
  children,
}: React.PropsWithChildren<ApplicationContextValue>) {
  const value = useMemo(
    () => ({
      application,
      userStatuses,
      featureFlags,
      termSetStatus,
      isFirstTimeUser: !!isFirstTimeUser,
    }),
    [application, userStatuses, featureFlags, isFirstTimeUser, termSetStatus],
  );
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export function useApplicationContext() {
  const contextValue = useContext(ApplicationContext);
  if (!contextValue) {
    throw new Error('Application data needs to be loaded first');
  }
  return contextValue;
}

export function useApplicationContextOrUndefined() {
  return useContext(ApplicationContext);
}

export function useFeatureFlags() {
  const { featureFlags } = useApplicationContext();
  return featureFlags;
}
